import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { formatGBPFromPounds } from '../../utils/currency';

function PricingCard({
  plan,
  billingCycle = 'monthly',
  isCurrentPlan = false,
  isCancelling = false,
  onSelect,
  onReactivate,
  onCancelRequest,
  showCancelSubscription = false,
  loading = false,
}) {
  // Calculate pricing based on billing cycle
  const getPricing = () => {
    if (billingCycle === 'yearly') {
      const discount = plan.annualDiscountPercent || 10;
      const annualPrice = plan.price * 12 * (1 - discount / 100);
      const savings = plan.price * 12 - annualPrice;
      const monthlyEquivalent = plan.price * (1 - discount / 100);
      
      return {
        price: annualPrice,
        period: 'year',
        displayPrice: formatGBPFromPounds(annualPrice),
        savings: savings,
        monthlyEquivalent: monthlyEquivalent,
        originalPrice: plan.price * 12,
      };
    }
    
    return {
      price: plan.price,
      period: 'month',
      displayPrice: formatGBPFromPounds(plan.price),
      savings: 0,
      monthlyEquivalent: plan.price,
      originalPrice: plan.price,
    };
  };

  const pricing = getPricing();

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        border: '1px solid',
        borderColor: isCurrentPlan ? 'primary.main' : 'divider',
        borderRadius: 1,
      }}
    >
      {isCurrentPlan && (
        <Chip
          label={isCancelling ? 'Cancelling' : 'Current'}
          color={isCancelling ? 'warning' : 'primary'}
          size="small"
          sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}
        />
      )}
      <CardContent
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          pt: isCurrentPlan ? 6 : 2,
        }}
      >
        <Typography variant="h5" component="h3" gutterBottom fontWeight={600}>
          {plan.displayName}
        </Typography>

        {plan.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {plan.description}
          </Typography>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" component="div" fontWeight={700}>
            {pricing.displayPrice}
            <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 0.5 }}>
              /{pricing.period}
            </Typography>
          </Typography>

          {billingCycle === 'yearly' && pricing.savings > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="success.main" fontWeight={600}>
                Save {formatGBPFromPounds(pricing.savings)} per year
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({formatGBPFromPounds(pricing.monthlyEquivalent)}/month)
              </Typography>
            </Box>
          )}
        </Box>

        {plan.features && plan.features.length > 0 && (
          <List dense sx={{ flexGrow: 1, mb: 2 }}>
            {plan.features.map((feature, index) => (
              <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText
                  primary={feature}
                  primaryTypographyProps={{
                    variant: 'body2',
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Box sx={{ mt: 'auto', pt: 2 }}>
          <Stack spacing={1}>
            {isCurrentPlan && isCancelling && onReactivate ? (
              <Button
                variant="contained"
                fullWidth
                disabled={loading}
                onClick={onReactivate}
              >
                Reactivate subscription
              </Button>
            ) : (
              <Button
                variant="outlined"
                fullWidth
                disabled={loading || (isCurrentPlan && !isCancelling)}
                onClick={() => onSelect(plan)}
              >
                {isCurrentPlan
                  ? 'Current plan'
                  : plan.price === 0
                    ? 'Downgrade'
                    : 'Select plan'}
              </Button>
            )}
            {showCancelSubscription && onCancelRequest && (
              <Button
                variant="outlined"
                color="error"
                fullWidth
                disabled={loading}
                onClick={onCancelRequest}
              >
                Cancel subscription
              </Button>
            )}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}

export default PricingCard;

